import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, Avatar, Snackbar, Alert,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { hub } from "../../api/requests/hubs/hubs";
import { Colors } from "../../Theme/Theme";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

export default function AdminKycManagement() {
  const [users, setUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchUsers = async () => {
    try {
      const response = await hub.getUsersWallet();
      setUsers(response?.users || []);
    } catch (error) {}
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleKycAction = async (userId, action) => {
    try {
      await hub.patchUpdateUserDetails({ user_id: userId, kyc_status: action });
      setSnackbar({ open: true, message: `KYC ${action === "APPROVED" ? "approved" : "rejected"} successfully!`, severity: "success" });
      fetchUsers();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.response?.data || "Failed to update KYC status. Please try again.";
      setSnackbar({ open: true, message: String(msg), severity: "error" });
    }
  };

  const getKycStatus = (user) => {
    const u = user?.user;
    if (u?.kyc_status === "APPROVED") return "APPROVED";
    if (u?.kyc_status === "REJECTED") return "REJECTED";
    if (u?.pan_number && u?.aadhar_number && u?.bank_account_number) return "PENDING";
    return "INCOMPLETE";
  };

  const statusConfig = {
    APPROVED: { color: Colors.insuranceGreen, bg: Colors.greenSoft, icon: <CheckCircleIcon sx={{ fontSize: 14 }} />, label: "Approved" },
    PENDING: { color: "#F59E0B", bg: "#FFFBEB", icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} />, label: "Pending Review" },
    REJECTED: { color: "#EF4444", bg: "#FEF2F2", icon: <CancelIcon sx={{ fontSize: 14 }} />, label: "Rejected" },
    INCOMPLETE: { color: "#94A3B8", bg: "#F1F5F9", icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} />, label: "Incomplete" },
  };

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1400 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <VerifiedUserIcon sx={{ fontSize: 22, color: Colors.primary }} />
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              KYC Management
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px" }}>
            Review and approve member KYC documents. Members cannot withdraw until KYC is approved.
          </Typography>
        </Box>

        {/* Stats */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          {[
            { label: "Total Members", value: users.length, color: Colors.primary },
            { label: "KYC Approved", value: users.filter(u => getKycStatus(u) === "APPROVED").length, color: Colors.insuranceGreen },
            { label: "Pending Review", value: users.filter(u => getKycStatus(u) === "PENDING").length, color: "#F59E0B" },
            { label: "Incomplete", value: users.filter(u => getKycStatus(u) === "INCOMPLETE").length, color: "#94A3B8" },
          ].map((stat, i) => (
            <Box key={i} sx={{ flex: "1 1 120px", p: 2, backgroundColor: Colors.white, borderRadius: "10px", border: `1px solid ${Colors.dividerColor}` }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
                {stat.label}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "22px", color: stat.color }}>{stat.value}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: Colors.primaryTextColor }}>
              Members KYC Status
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {["Member", "PAN", "Aadhaar", "Bank Account", "IFSC", "Level", "KYC Status", "Actions"].map((h) => (
                    <TableCell key={h} sx={{
                      color: Colors.secondaryColor, py: 1.5, px: 2,
                      fontWeight: 600, fontSize: "11px", letterSpacing: "0.5px",
                      textTransform: "uppercase", backgroundColor: Colors.bgColor,
                      borderBottom: `1px solid ${Colors.dividerColor}`, whiteSpace: "nowrap",
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 6, textAlign: "center", borderBottom: "none" }}>
                      <Typography sx={{ color: Colors.light_gray, fontSize: "14px" }}>No members found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((item) => {
                    const u = item?.user;
                    const status = getKycStatus(item);
                    const sc = statusConfig[status];
                    return (
                      <TableRow key={u?.id} sx={{ "&:hover": { backgroundColor: Colors.bgColor } }}>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{ width: 32, height: 32, backgroundColor: Colors.blueSoft, color: Colors.primary, fontSize: "12px", fontWeight: 700 }}>
                              {u?.name?.charAt(0)?.toUpperCase() || "U"}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: "13px", color: Colors.primaryTextColor }}>{u?.name || "---"}</Typography>
                              <Typography sx={{ fontSize: "11px", color: Colors.secondaryColor }}>{u?.email || "---"}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2 }}>
                          <Typography sx={{ fontSize: "12px", color: u?.pan_number ? Colors.primaryTextColor : Colors.light_gray, fontFamily: "monospace" }}>
                            {u?.pan_number || "Not provided"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2 }}>
                          <Typography sx={{ fontSize: "12px", color: u?.aadhar_number ? Colors.primaryTextColor : Colors.light_gray, fontFamily: "monospace" }}>
                            {u?.aadhar_number || "Not provided"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2 }}>
                          <Typography sx={{ fontSize: "12px", color: u?.bank_account_number ? Colors.primaryTextColor : Colors.light_gray, fontFamily: "monospace" }}>
                            {u?.bank_account_number || "Not provided"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2 }}>
                          <Typography sx={{ fontSize: "12px", color: u?.ifsc_code ? Colors.primaryTextColor : Colors.light_gray, fontFamily: "monospace" }}>
                            {u?.ifsc_code || "---"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2 }}>
                          <Chip label={`Level ${item?.level || "---"}`} size="small" sx={{
                            height: 22, fontSize: "11px", fontWeight: 600,
                            backgroundColor: Colors.blueSoft, color: Colors.primary,
                          }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2 }}>
                          <Chip
                            icon={sc.icon}
                            label={sc.label}
                            size="small"
                            sx={{
                              height: 24, fontSize: "11px", fontWeight: 600,
                              backgroundColor: sc.bg, color: sc.color,
                              "& .MuiChip-icon": { color: sc.color },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2 }}>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {status !== "APPROVED" && status !== "INCOMPLETE" && (
                              <Button
                                size="small" variant="contained"
                                onClick={() => handleKycAction(u?.id, "APPROVED")}
                                sx={{
                                  backgroundColor: Colors.insuranceGreen, fontSize: "11px",
                                  fontWeight: 600, borderRadius: "6px", textTransform: "none",
                                  px: 1.5, py: 0.3, boxShadow: "none", minWidth: 0,
                                  "&:hover": { backgroundColor: "#047857" },
                                }}
                              >
                                Approve
                              </Button>
                            )}
                            {status !== "REJECTED" && status !== "INCOMPLETE" && (
                              <Button
                                size="small" variant="outlined"
                                onClick={() => handleKycAction(u?.id, "REJECTED")}
                                sx={{
                                  borderColor: "#FCA5A5", color: "#EF4444", fontSize: "11px",
                                  fontWeight: 600, borderRadius: "6px", textTransform: "none",
                                  px: 1.5, py: 0.3, minWidth: 0,
                                  "&:hover": { backgroundColor: "#FEF2F2" },
                                }}
                              >
                                Reject
                              </Button>
                            )}
                            {status === "INCOMPLETE" && (
                              <Typography sx={{ fontSize: "11px", color: Colors.light_gray, fontStyle: "italic" }}>
                                Awaiting details
                              </Typography>
                            )}
                            {status === "APPROVED" && (
                              <Typography sx={{ fontSize: "11px", color: Colors.insuranceGreen, fontWeight: 600 }}>
                                Verified
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
